import { useQuery } from '@tanstack/react-query';
import { BRAND_COLORS, type MeResponse } from '@lk/shared';
import { Icon, SUBJECT_ICON } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { EmptyState, ErrorState, LoadingState } from '../../components/States';
import { formatDate } from '../../lib/format';
import { mistakesQuery } from '../learner/queries';
import { useSelectedLearner } from './ParentLayout';
import { QuestionText, figureMaxWidth } from '../../components/QuestionText';
import { SafeSvg, looksLikeSvg } from '../../components/SafeSvg';
import { speechTextOf, stripSpeechLines } from '../../audio/speech';

export function MistakesPage({ me }: { me: MeResponse }): React.JSX.Element {
  const learner = useSelectedLearner(me);
  const mistakes = useQuery({
    ...mistakesQuery(learner?.id ?? ''),
    enabled: Boolean(learner?.id),
  });

  if (!learner) return <EmptyState title="Немає дитини для показу" />;
  if (mistakes.isLoading) return <LoadingState label="Збираємо помилки…" />;
  if (mistakes.isError) {
    return <ErrorState error={mistakes.error} onRetry={() => void mistakes.refetch()} />;
  }

  const items = mistakes.data?.items ?? [];
  const unresolved = items.filter((item) => !item.resolved);
  const resolved = items.filter((item) => item.resolved);

  if (items.length === 0) {
    return (
      <EmptyState
        icon="correct"
        title="Помилок немає"
        hint="Або їх ще не встигли зробити, або всі вже виправлені."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Panel className="p-4">
        <h2 className="text-lg font-black">Помилки · {learner.displayName}</h2>
        <p className="mt-1 text-xs font-semibold text-white/55">
          {unresolved.length} ще не виправлено, {resolved.length} вже опрацьовано. Виправлена
          помилка приносить дитині окремий бонус — це найцінніша подія в системі.
        </p>
      </Panel>

      {[...unresolved, ...resolved].map((item) => (
        <Panel
          key={`${item.itemId}-${item.lastWrongAt}`}
          tight
          className="p-4"
          style={{ borderColor: item.resolved ? `${BRAND_COLORS.green}55` : `${BRAND_COLORS.red}66` }}
        >
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-black text-white/50 uppercase">
            <Icon name={SUBJECT_ICON[item.subject]} size={16} />
            <span>{item.skillTitle}</span>
            <span className="text-white/30">·</span>
            <span>{formatDate(item.lastWrongAt)}</span>
            <span className="text-white/30">·</span>
            <span>помилок: {item.timesWrong}</span>
            <div className="flex-1" />
            {item.resolved ? (
              <span className="lk-chip border-[var(--lk-green)] text-[10px] text-[var(--lk-green)]">
                виправлено
              </span>
            ) : (
              <span className="lk-chip border-[var(--lk-red)] text-[10px] text-[var(--lk-red)]">
                в роботі
              </span>
            )}
          </div>

          <p className="mt-2 text-sm font-black">
            <QuestionText text={stripSpeechLines(item.prompt)} />
          </p>
          {/*
            Аудіозавдання. Дитині фразу лише озвучують, а батькові її треба
            саме показати: інакше в розборі помилок стоїть питання без умови.
            Службовий маркер прибираємо — в інтерфейсі емодзі не місце.
          */}
          {speechTextOf(item.passage) ? (
            <p className="mt-1.5 text-xs font-bold text-white/70">
              звучала фраза: {speechTextOf(item.passage)}
            </p>
          ) : null}
          {/* Малюнок-умова: без нього «Продовж ряд» батькові нічого не каже. */}
          {item.passage && looksLikeSvg(item.passage) ? (
            <span
              className="mx-auto mt-2 block w-full"
              style={{ maxWidth: figureMaxWidth(item.passage) }}
            >
              <SafeSvg markup={item.passage} label="Малюнок до питання" />
            </span>
          ) : null}
          <p className="mt-1.5 text-xs font-bold text-[var(--lk-red)]">
            відповіла дитина: {item.givenAnswer}
          </p>
          <p className="text-xs font-bold text-[var(--lk-green)]">правильно: {item.correctAnswer}</p>
          <p className="mt-2 text-xs leading-relaxed font-semibold text-white/75">
            {item.explanation}
          </p>
        </Panel>
      ))}
    </div>
  );
}
